import { Fragment, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
  FaDownload,
  FaQrcode,
  FaSyncAlt,
  FaTrash,
  FaTimes,
} from 'react-icons/fa';
import {
  deleteWaitlistReferralLink,
  createWaitlistReferralLink,
  getWaitlistReferralLinks,
} from '../../services/SiteRequestService';
import type {
  CreateWaitlistReferralLinkResponse,
  WaitlistReferralLink,
} from '../../types/siteRequest.types';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import {
  DEFAULT_HIGH_RES_SIZE,
  downloadCanvasAsPng,
  getLogoDimensions,
  QR_BG_COLOR,
  QR_BRAND_COLOR,
} from '../../utils/qrCodeGenerator';

const WAITLIST_PAGE_URL = 'https://www.e2gfood.com/tap2feed-waitlist';

type QrDestination = 'WAITLIST' | 'REGISTER_INDIVIDUAL' | 'REGISTER_AMBASSADOR' | 'REGISTER_ORGANISATION';

const DESTINATIONS: Record<QrDestination, { label: string; path: string; filePrefix: string }> = {
  WAITLIST: { label: 'Waitlist', path: '/tap2feed-waitlist', filePrefix: 'tap2feed-waitlist' },
  REGISTER_INDIVIDUAL: { label: 'Registration — Individual', path: '/tap2feed-register/individual', filePrefix: 'tap2feed-register-individual' },
  REGISTER_AMBASSADOR: { label: 'Registration — Ambassador', path: '/tap2feed-register/ambassador', filePrefix: 'tap2feed-register-ambassador' },
  REGISTER_ORGANISATION: { label: 'Registration — Organisation', path: '/tap2feed-register/organisation', filePrefix: 'tap2feed-register-organisation' },
};
const PAGE_SIZE = 10;
const QR_PREVIEW_SIZE = 256;
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/TYI_ICON_GREEN.png`;

function formatCreatedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

function buildReferralUrl(token: string, destination: QrDestination): string {
  const url = new URL(WAITLIST_PAGE_URL);
  url.pathname = DESTINATIONS[destination].path;
  url.searchParams.set('ref', token);
  return url.toString();
}

function getSafeFilename(name: string, destination: QrDestination): string {
  const safeName = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${DESTINATIONS[destination].filePrefix}-${safeName || 'referral'}.png`;
}

export default function WaitlistReferralGenerator() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [destination, setDestination] = useState<QrDestination>('WAITLIST');
  const [generatedReferral, setGeneratedReferral] =
    useState<CreateWaitlistReferralLinkResponse | null>(null);
  const [referralLinks, setReferralLinks] = useState<WaitlistReferralLink[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reissuingId, setReissuingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewRowRef = useRef<HTMLTableRowElement>(null);

  const loadReferralLinks = useCallback(async (requestedPage: number) => {
    setIsLoading(true);
    setListError(null);
    try {
      const result = await getWaitlistReferralLinks({ page: requestedPage, limit: PAGE_SIZE });
      setReferralLinks(result.links);
      setTotal(result.total);
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : 'Unable to load waitlist referral QR codes.';
      setListError(message);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReferralLinks(page);
  }, [loadReferralLinks, page]);

  const selectedReferral = generatedReferral;
  const selectedUrl = selectedReferral ? buildReferralUrl(selectedReferral.token, destination) : '';
  const selectedReferralId = selectedReferral?.id ?? null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const previewLogo = getLogoDimensions(QR_PREVIEW_SIZE * 0.25);
  const downloadLogo = getLogoDimensions(DEFAULT_HIGH_RES_SIZE * 0.25);

  useEffect(() => {
    previewRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedReferralId]);

  const generateReferral = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsGenerating(true);
    setError(null);

    try {
      const result = await createWaitlistReferralLink({
        name: name.trim(),
        email: email.trim(),
      });
      showSuccessToast('Waitlist referral QR code generated.');
      setPage(1);
      await loadReferralLinks(1);
      setGeneratedReferral(result);
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : 'Unable to generate the waitlist referral QR code.';
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyReferralUrl = async () => {
    if (!selectedUrl) return;
    try {
      await navigator.clipboard.writeText(selectedUrl);
      showSuccessToast('Referral link copied.');
    } catch {
      setError('Unable to copy the referral link. You can select and copy it manually.');
    }
  };

  const downloadQrCode = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas || !selectedReferral) {
      setError('The QR code is not ready to download.');
      return;
    }
    downloadCanvasAsPng(canvas, getSafeFilename(selectedReferral.name, destination));
    showSuccessToast('QR downloaded.');
  };

  const createReplacement = async (referral: WaitlistReferralLink) => {
    setReissuingId(referral.id);
    setError(null);
    try {
      const replacement = await createWaitlistReferralLink({
        name: referral.name,
        email: referral.email,
      });
      showSuccessToast('Replacement QR created. The previous QR link remains active.');
      setPage(1);
      await loadReferralLinks(1);
      setGeneratedReferral(replacement);
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : 'Unable to create a replacement waitlist QR code.';
      setError(message);
    } finally {
      setReissuingId(null);
    }
  };

  const removeReferral = async (referral: WaitlistReferralLink) => {
    if (!window.confirm(`Delete the waitlist QR code for ${referral.name}? Its link will stop working.`)) {
      return;
    }

    setDeletingId(referral.id);
    setListError(null);
    try {
      await deleteWaitlistReferralLink(referral.id);
      if (generatedReferral?.id === referral.id) {
        setGeneratedReferral(null);
      }
      showSuccessToast('Waitlist referral QR code deleted.');

      if (referralLinks.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await loadReferralLinks(page);
      }
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : 'Unable to delete the waitlist referral QR code.';
      setListError(message);
      showErrorToast(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 dark:border-amber-900/60 dark:bg-amber-950/20 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500 text-white">
          <FaQrcode aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create a waitlist QR code</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Attribute signups to a referrer without showing their email in the QR link.
          </p>
        </div>
      </div>

      <form
        onSubmit={generateReferral}
        className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
      >
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Referrer name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            maxLength={255}
            required
            className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 dark:border-white/10 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Referrer email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            maxLength={255}
            required
            className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 dark:border-white/10 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Destination
          <select
            value={destination}
            onChange={(event) => setDestination(event.target.value as QrDestination)}
            className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 dark:border-white/10 dark:bg-slate-900"
          >
            {(Object.keys(DESTINATIONS) as QrDestination[]).map((key) => (
              <option key={key} value={key}>{DESTINATIONS[key].label}</option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={isGenerating}
          className="rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isGenerating ? 'Generating…' : 'Generate QR code'}
        </button>
      </form>

      {error && (
        <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </p>
      )}

      <div className="mt-7 border-t border-amber-200 pt-5 dark:border-amber-900/60">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Generated waitlist QR codes</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {total.toLocaleString()} code{total === 1 ? '' : 's'} total
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadReferralLinks(page)}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
          >
            <FaSyncAlt className={isLoading ? 'animate-spin' : ''} aria-hidden="true" />
            Refresh
          </button>
        </div>

        {listError && (
          <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            Could not load the QR code list: {listError}
          </p>
        )}

        {isLoading ? (
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">Loading generated QR codes…</p>
        ) : referralLinks.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-amber-300 bg-white/70 px-4 py-6 text-center text-sm text-gray-600 dark:border-amber-900 dark:bg-slate-900/30 dark:text-gray-300">
            No waitlist QR codes have been generated yet.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white/70 dark:border-white/10 dark:bg-slate-900/30">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <thead className="bg-white text-xs uppercase tracking-wide text-gray-500 dark:bg-slate-800 dark:text-gray-300">
                <tr>
                  <th scope="col" className="px-4 py-3">Referrer</th>
                  <th scope="col" className="px-4 py-3">Generated</th>
                  <th scope="col" className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                {referralLinks.map((referral) => {
                  const token = referral.token;
                  const isSelected = selectedReferralId === referral.id && Boolean(token);

                  return (
                    <Fragment key={referral.id}>
                      <tr className="text-gray-700 dark:text-gray-200">
                        <td className="px-4 py-3">
                          <p className="font-semibold">{referral.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{referral.email}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                          {formatCreatedAt(referral.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {token ? (
                              <button
                                type="button"
                                aria-expanded={isSelected}
                                aria-controls={`waitlist-qr-preview-${referral.id}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setGeneratedReferral(null);
                                  } else {
                                    setGeneratedReferral({
                                      id: referral.id,
                                      name: referral.name,
                                      email: referral.email,
                                      createdAt: referral.createdAt,
                                      token,
                                    });
                                  }
                                }}
                                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
                              >
                                {isSelected ? (
                                  <FaTimes aria-hidden="true" />
                                ) : (
                                  <FaQrcode aria-hidden="true" />
                                )}
                                {isSelected ? 'Close preview' : 'Preview QR'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => void createReplacement(referral)}
                                disabled={reissuingId === referral.id}
                                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <FaQrcode aria-hidden="true" />
                                {reissuingId === referral.id ? 'Creating…' : 'Create replacement'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => void removeReferral(referral)}
                              disabled={deletingId === referral.id}
                              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:bg-slate-900 dark:text-red-300"
                            >
                              <FaTrash aria-hidden="true" />
                              {deletingId === referral.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                          {!token && (
                            <p className="mt-1 max-w-sm text-xs text-amber-700 dark:text-amber-300">
                              This older code cannot be re-downloaded because its original token was not retained.
                            </p>
                          )}
                        </td>
                      </tr>

                      {isSelected && selectedReferral && (
                        <tr
                          ref={previewRowRef}
                          id={`waitlist-qr-preview-${referral.id}`}
                          className="bg-amber-50/70 dark:bg-amber-950/20"
                        >
                          <td colSpan={3} className="border-t border-amber-200 p-4 dark:border-amber-900/60 sm:p-6">
                            <div className="grid gap-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center">
                              <div className="w-fit rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                <QRCodeCanvas
                                  value={selectedUrl}
                                  size={QR_PREVIEW_SIZE}
                                  marginSize={4}
                                  level="H"
                                  fgColor={QR_BRAND_COLOR}
                                  bgColor={QR_BG_COLOR}
                                  imageSettings={{
                                    src: LOGO_SRC,
                                    height: previewLogo.height,
                                    width: previewLogo.width,
                                    excavate: true,
                                  }}
                                />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  Referral QR for {selectedReferral.name} ({selectedReferral.email})
                                </p>
                                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                                  Destination: {DESTINATIONS[destination].label} (change it in the form above)
                                </p>
                                <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  Referral link
                                  <input
                                    readOnly
                                    value={selectedUrl}
                                    onFocus={(event) => event.currentTarget.select()}
                                    className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-700 dark:border-white/10 dark:bg-slate-900 dark:text-gray-200"
                                  />
                                </label>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => void copyReferralUrl()}
                                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 dark:hover:bg-slate-700"
                                  >
                                    <FaCopy aria-hidden="true" />
                                    Copy link
                                  </button>
                                  <button
                                    type="button"
                                    onClick={downloadQrCode}
                                    className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                                  >
                                    <FaDownload aria-hidden="true" />
                                    Download QR
                                  </button>
                                </div>
                              </div>
                              <div
                                aria-hidden="true"
                                className="pointer-events-none fixed -left-[10000px] top-0 opacity-0"
                              >
                                <QRCodeCanvas
                                  ref={qrCanvasRef}
                                  value={selectedUrl}
                                  size={DEFAULT_HIGH_RES_SIZE}
                                  marginSize={4}
                                  level="H"
                                  fgColor={QR_BRAND_COLOR}
                                  bgColor={QR_BG_COLOR}
                                  imageSettings={{
                                    src: LOGO_SRC,
                                    height: downloadLogo.height,
                                    width: downloadLogo.width,
                                    excavate: true,
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || isLoading}
              aria-label="Previous page of QR codes"
              className="rounded-lg border border-gray-300 bg-white p-2 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
            >
              <FaChevronLeft aria-hidden="true" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages || isLoading}
              aria-label="Next page of QR codes"
              className="rounded-lg border border-gray-300 bg-white p-2 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
